/**
 * Usage Tracking Service for Fidora
 * Tracks text tokens and voice characters per user per month
 */

import { doc, getDoc, setDoc, updateDoc, increment, serverTimestamp, getFirestore } from 'firebase/firestore';
import { initializeApp, getApps } from 'firebase/app';

// Get existing Firebase instance or use default
let db;
try {
  const apps = getApps();
  if (apps.length > 0) {
    db = getFirestore(apps[0]);
    console.log('✅ Firestore initialized in usageTracking');
  } else {
    // Firebase should already be initialized in App.js
    // Silent fallback - will be set when App.js loads
    db = null;
  }
} catch (error) {
  // Silent - Firebase will be initialized by App.js
  db = null;
}

/**
 * Set the Firestore database instance
 * Call this from App.js after Firebase is initialized
 */
export function setFirestore(firestoreDb) {
  db = firestoreDb;
}

// Tier limits (monthly) - UPDATED WITH CORRECT VALUES
// Voice calculation: hours × 60 min/hr × 150 words/min × 6 chars/word = total chars
export const TIER_LIMITS = {
  free: {
    textTokens: 5000,          // ~5-7 conversations
    voiceCharacters: 1800,     // ~2 minutes (150 words/min × 2 min × 6 chars/word)
    conversations: 7,           // 7 conversations for free trial
    voiceMinutes: 2,
    mockInterviews: 0          // No mock interviews in free trial
  },
  advisor: {
    textTokens: 20000,         // Text-only tier
    voiceCharacters: 0,        // NO VOICE
    conversations: 200,         // 200 conversations/month
    voiceMinutes: 0,
    mockInterviews: 0          // No mock interviews in advisor tier
  },
  coach: {
    textTokens: 75000,         // ~75-100 conversations
    voiceCharacters: 162000,   // 3 hours (180 min × 150 words/min × 6 chars/word)
    conversations: 300,         // 300 conversations/month
    voiceMinutes: 180,         // 3 hours
    mockInterviews: 2          // 2 mock interviews per month
  },
  mentor: {
    textTokens: 150000,        // ~150-200 conversations
    voiceCharacters: 324000,   // 6 hours (360 min × 150 words/min × 6 chars/word)
    conversations: 500,         // 500 conversations/month
    voiceMinutes: 360,         // 6 hours
    mockInterviews: 4          // 4 mock interviews per month
  }
};

/**
 * Get user's current usage WITH SUBSCRIPTION STATUS CHECK
 * This is CRITICAL - checks if subscription expired, cancelled, or needs reset
 */
export async function getUserUsage(userId) {
  try {
    if (!db) {
      throw new Error('Firestore not initialized. Please ensure Firebase is initialized before calling getUserUsage.');
    }
    
    // Get user document (has subscription info)
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      // Initialize new user with free tier
      const initialUser = {
        userId,
        email: '',
        subscriptionTier: 'free',
        subscriptionStatus: 'active',
        currentPeriodStart: new Date().toISOString(),
        currentPeriodEnd: addDays(new Date(), 7).toISOString(), // 7-day free trial
        cancelAtPeriodEnd: false,
      usage: {
        textTokensUsed: 0,
        voiceCharactersUsed: 0,
        conversationCount: 0,
        mockInterviewsUsed: 0,
        textTokensLimit: TIER_LIMITS.free.textTokens,
        voiceCharactersLimit: TIER_LIMITS.free.voiceCharacters,
        conversationLimit: TIER_LIMITS.free.conversations,
        mockInterviewsLimit: TIER_LIMITS.free.mockInterviews,
        lastResetDate: new Date().toISOString()
      },
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      };
      await setDoc(userRef, initialUser);
      return initialUser;
    }
    
    const userData = userDoc.data();
    
    // 🚨 CRITICAL: Check if subscription expired or cancelled
    const now = new Date();
    const periodEnd = new Date(userData.currentPeriodEnd);
    
    // If period ended AND subscription was cancelled, downgrade to free
    if (userData.cancelAtPeriodEnd && now > periodEnd) {
      console.warn(`⚠️ Subscription expired for user ${userId}, downgrading to free`);
      await updateDoc(userRef, {
        subscriptionTier: 'free',
        subscriptionStatus: 'expired',
        'usage.textTokensLimit': TIER_LIMITS.free.textTokens,
        'usage.voiceCharactersLimit': TIER_LIMITS.free.voiceCharacters,
        'usage.conversationLimit': TIER_LIMITS.free.conversations,
        lastUpdated: serverTimestamp()
      });
      userData.subscriptionTier = 'free';
      userData.subscriptionStatus = 'expired';
      userData.usage.textTokensLimit = TIER_LIMITS.free.textTokens;
      userData.usage.voiceCharactersLimit = TIER_LIMITS.free.voiceCharacters;
      userData.usage.conversationLimit = TIER_LIMITS.free.conversations;
    }
    
    // 🔄 Check if usage needs to be reset (new billing period)
    if (now > periodEnd && userData.subscriptionStatus === 'active') {
      console.log(`🔄 Resetting usage for user ${userId} - new billing period`);
      const tier = userData.subscriptionTier || 'free';
      const limits = TIER_LIMITS[tier];
      
      await updateDoc(userRef, {
        'usage.textTokensUsed': 0,
        'usage.voiceCharactersUsed': 0,
        'usage.conversationCount': 0,
        'usage.mockInterviewsUsed': 0,
        'usage.lastResetDate': now.toISOString(),
        currentPeriodStart: now.toISOString(),
        currentPeriodEnd: addDays(now, 30).toISOString(),
        lastUpdated: serverTimestamp()
      });
      
      userData.usage.textTokensUsed = 0;
      userData.usage.voiceCharactersUsed = 0;
      userData.usage.conversationCount = 0;
      userData.usage.mockInterviewsUsed = 0;
      userData.usage.lastResetDate = now.toISOString();
      userData.currentPeriodStart = now.toISOString();
      userData.currentPeriodEnd = addDays(now, 30).toISOString();
    }
    
    return userData;
  } catch (error) {
    console.error('Error getting user usage:', error);
    throw error;
  }
}

// Helper function to add days to a date
function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Check if user has available quota - WITH SUBSCRIPTION STATUS ENFORCEMENT
 * Returns whether user is allowed to use the service
 */
export async function checkUsageLimit(userId, type = 'text') {
  try {
    if (!db) {
      console.error('❌ Firestore not initialized in checkUsageLimit');
      return { 
        allowed: false, 
        reason: 'Service temporarily unavailable. Please refresh the page.',
        error: 'Firestore not initialized'
      };
    }
    
    const userData = await getUserUsage(userId); // This checks subscription status
    const usage = userData.usage || {};
    const tier = userData.subscriptionTier || 'free';
    const limits = TIER_LIMITS[tier];
    
    // 🚨 CRITICAL: Enforce subscription status
    if (userData.subscriptionStatus === 'expired') {
      return {
        allowed: false,
        reason: 'Subscription expired. Please renew to continue.',
        used: usage[type === 'text' ? 'textTokensUsed' : 'voiceCharactersUsed'] || 0,
        limit: 0,
        remaining: 0,
        percentUsed: 100,
        upgradeRequired: true
      };
    }
    
    if (userData.subscriptionStatus === 'past_due') {
      return {
        allowed: false,
        reason: 'Payment failed. Please update your payment method.',
        used: usage[type === 'text' ? 'textTokensUsed' : 'voiceCharactersUsed'] || 0,
        limit: 0,
        remaining: 0,
        percentUsed: 100,
        paymentRequired: true
      };
    }
    
    if (type === 'text') {
      const used = usage.textTokensUsed || 0;
      const limit = usage.textTokensLimit || limits.textTokens;
      return {
        allowed: used < limit,
        used,
        limit,
        remaining: Math.max(0, limit - used),
        percentUsed: (used / limit) * 100,
        tier
      };
    } else if (type === 'voice') {
      const used = usage.voiceCharactersUsed || 0;
      const limit = usage.voiceCharactersLimit || limits.voiceCharacters;
      
      // Advisor tier has NO voice
      if (tier === 'advisor') {
        return {
          allowed: false,
          reason: 'Voice not available on Advisor tier. Upgrade to Coach for voice coaching.',
          used: 0,
          limit: 0,
          remaining: 0,
          percentUsed: 100,
          upgradeRequired: true,
          tier
        };
      }
      
      return {
        allowed: used < limit,
        used,
        limit,
        remaining: Math.max(0, limit - used),
        percentUsed: limit > 0 ? (used / limit) * 100 : 100,
        tier
      };
    }
  } catch (error) {
    console.error('Error checking usage limit:', error);
    // 🚨 FAIL CLOSED - Don't allow if error (prevents abuse)
    return { 
      allowed: false, 
      error: error.message,
      reason: 'Unable to verify subscription status. Please try again.'
    };
  }
}

/**
 * Track text message usage
 * @param {string} userId - User ID
 * @param {number} tokensUsed - Total tokens (input + output)
 */
export async function trackTextUsage(userId, tokensUsed) {
  if (!userId) {
    console.error('❌ CRITICAL: No userId provided to trackTextUsage!');
    return;
  }
  
  if (!db) {
    console.error('❌ CRITICAL: Firestore not initialized in trackTextUsage!');
    return;
  }
  
  try {
    console.log(`📊 Tracking text usage for ${userId}: ${tokensUsed} tokens`);
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      'usage.textTokensUsed': increment(tokensUsed),
      lastUpdated: serverTimestamp()
    });
    console.log('✅ Text usage tracked successfully');
  } catch (error) {
    console.error('❌ CRITICAL ERROR tracking text usage:', error);
    console.error('Failed to track:', { userId, tokensUsed, error: error.message });
    // Don't throw - usage tracking shouldn't break the app
    // But log it loudly for monitoring
  }
}

/**
 * Track voice usage
 * @param {string} userId - User ID
 * @param {number} characters - Character count of text sent to TTS
 */
export async function trackVoiceUsage(userId, characters) {
  if (!userId) {
    console.error('❌ CRITICAL: No userId provided to trackVoiceUsage!');
    return;
  }
  
  if (!db) {
    console.error('❌ CRITICAL: Firestore not initialized in trackVoiceUsage!');
    return;
  }
  
  try {
    console.log(`🎤 Tracking voice usage for ${userId}: ${characters} characters (~${(characters/900).toFixed(2)} min)`);
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      'usage.voiceCharactersUsed': increment(characters),
      lastUpdated: serverTimestamp()
    });
    console.log('✅ Voice usage tracked successfully');
  } catch (error) {
    console.error('❌ CRITICAL ERROR tracking voice usage:', error);
    console.error('Failed to track:', { userId, characters, error: error.message });
    // Don't throw - usage tracking shouldn't break the app
    // But log it loudly for monitoring
  }
}

/**
 * Track mock interview usage
 * @param {string} userId - User ID
 * @returns {Promise<boolean>} Whether tracking was successful
 */
export async function trackMockInterviewUsage(userId) {
  if (!userId) {
    console.error('❌ CRITICAL: No userId provided to trackMockInterviewUsage!');
    return false;
  }
  
  if (!db) {
    console.error('❌ CRITICAL: Firestore not initialized in trackMockInterviewUsage!');
    return false;
  }
  
  try {
    console.log(`🎤 Tracking mock interview usage for ${userId}`);
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      'usage.mockInterviewsUsed': increment(1),
      lastUpdated: serverTimestamp()
    });
    console.log('✅ Mock interview usage tracked successfully');
    return true;
  } catch (error) {
    console.error('❌ CRITICAL ERROR tracking mock interview usage:', error);
    console.error('Failed to track:', { userId, error: error.message });
    return false;
  }
}

/**
 * Check mock interview quota
 * @param {string} userId - User ID
 * @returns {Promise<{allowed: boolean, used: number, limit: number, remaining: number}>}
 */
export async function checkMockInterviewQuota(userId) {
  try {
    if (!db) {
      console.error('❌ Firestore not initialized in checkMockInterviewQuota');
      return {
        allowed: false,
        error: 'Firestore not initialized',
        reason: 'Service temporarily unavailable. Please refresh the page.'
      };
    }
    
    const userData = await getUserUsage(userId);
    const usage = userData.usage || {};
    const tier = userData.subscriptionTier || 'free';
    const limits = TIER_LIMITS[tier];
    
    const used = usage.mockInterviewsUsed || 0;
    const limit = limits.mockInterviews || 0;
    
    // Check subscription status
    if (userData.subscriptionStatus === 'expired' || userData.subscriptionStatus === 'past_due') {
      return {
        allowed: false,
        reason: 'Subscription inactive. Please renew to access mock interviews.',
        used,
        limit: 0,
        remaining: 0,
        upgradeRequired: true,
        tier
      };
    }
    
    // Free and Advisor tiers don't have mock interviews
    if (tier === 'free' || tier === 'advisor') {
      return {
        allowed: false,
        reason: `Mock interviews are not available on ${tier} tier. Upgrade to Coach or Mentor to access mock interviews.`,
        used: 0,
        limit: 0,
        remaining: 0,
        upgradeRequired: true,
        tier
      };
    }
    
    return {
      allowed: used < limit,
      used,
      limit,
      remaining: Math.max(0, limit - used),
      tier
    };
  } catch (error) {
    console.error('Error checking mock interview quota:', error);
    return {
      allowed: false,
      error: error.message,
      reason: 'Unable to verify mock interview quota. Please try again.'
    };
  }
}

/**
 * Update user's subscription tier
 */
export async function updateSubscriptionTier(userId, tier) {
  try {
    const usageRef = doc(db, 'usage', userId);
    await updateDoc(usageRef, {
      subscriptionTier: tier,
      lastUpdated: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating subscription tier:', error);
    throw error;
  }
}

/**
 * Get usage summary for display
 */
export function getUsageSummary(usage) {
  const tier = usage.subscriptionTier || 'free';
  const limits = TIER_LIMITS[tier];
  
  return {
    tier,
    text: {
      messages: usage.textMessagesCount || 0,
      messagesLimit: limits.textMessages,
      tokens: usage.textTokensUsed || 0,
      tokensLimit: limits.textTokens,
      percentUsed: ((usage.textTokensUsed || 0) / limits.textTokens) * 100
    },
    voice: {
      minutes: usage.voiceMinutesUsed || 0,
      minutesLimit: limits.voiceMinutes,
      characters: usage.voiceCharactersUsed || 0,
      charactersLimit: limits.voiceCharacters,
      percentUsed: ((usage.voiceCharactersUsed || 0) / limits.voiceCharacters) * 100
    },
    periodStart: usage.periodStart,
    shouldUpgrade: (
      ((usage.textTokensUsed || 0) / limits.textTokens) > 0.8 ||
      ((usage.voiceCharactersUsed || 0) / limits.voiceCharacters) > 0.8
    )
  };
}

export default {
  TIER_LIMITS,
  getUserUsage,
  checkUsageLimit,
  trackTextUsage,
  trackVoiceUsage,
  trackMockInterviewUsage,
  checkMockInterviewQuota,
  updateSubscriptionTier,
  getUsageSummary
};

